/**
 * CUS-2 — per-ticket custody handoff history projection.
 *
 * `useTicketCustodyEvents` deliberately drops actor / role / reason (it
 * only feeds `deriveTimeBuckets`, which needs kind + timestamp). This
 * pure module projects the FULL custody-event row into a display-ready
 * handoff history that preserves WHO did the handoff, their role, and
 * WHY — the accountability trail the data already records but no surface
 * showed.
 *
 * Pure: no I/O. The reason is the FRIENDLY label: the label recorded with the
 * handoff (metadata.trigger_reason_label), else the label of the explicit
 * `reason` column / metadata reason value — never a raw snake_case code.
 *
 * fix/foh-optiboard-calendar-truth: entries also carry the technician's
 * free-form note (metadata.notes) and the event id, and
 * `latestMeaningfulHandoff` picks the handoff that explains why a ticket is
 * where it is — the latest KICK, never the accept that followed it.
 */
import { getCustodyHandoffReasonLabel, readCustodyHandoffMetadata } from './custodyHandoffReasons';
import type { CustodyEventKind } from './types';

export const CUSTODY_EVENT_KIND_LABELS: Record<CustodyEventKind, string> = {
  kick_to_foh: 'Sent to advisor (FOH)',
  foh_accept: 'Advisor accepted',
  kick_to_boh: 'Returned to tech (BOH)',
  boh_accept: 'Tech accepted return',
  reject_back: 'Handoff rejected',
  reassign_within_foh: 'Reassigned (advisor → advisor)',
  reassign_within_boh: 'Reassigned (tech → tech)',
  carried_by_lineage: 'Custody carried from the original ticket (split / rollover / merge)',
  customer_pickup: 'Customer picked up the vehicle — shop custody closed',
  vehicle_returned: 'Vehicle returned to the shop — received by the front office',
};

/** Raw shape selected from ticket_custody_events (CUS-2 full read). */
export interface CustodyHistoryRow {
  id?: string | null;
  event_kind: string;
  from_owner: string | null;
  to_owner: string | null;
  actor_id: string | null;
  actor_role: string | null;
  reason: string | null;
  metadata?: unknown;
  created_at: string;
}

export interface CustodyHistoryEntry {
  eventKind: CustodyEventKind;
  label: string;
  fromOwner: string | null;
  toOwner: string | null;
  actorId: string | null;
  actorRole: string | null;
  /** Friendly reason label (never a raw code), or null when none was given. */
  reason: string | null;
  /** The person's free-form note ("Anything FOH should know"), if any. */
  notes: string | null;
  eventId: string | null;
  createdAt: string;
}

/** The free-form note a handoff carried (metadata.notes), trimmed. */
export function custodyEventNotes(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const n = (metadata as Record<string, unknown>).notes;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}

/** The friendly reason of a custody event, or null when it carried none. */
export function custodyEventReasonLabel(reason: string | null | undefined, metadata: unknown): string | null {
  const meta = readCustodyHandoffMetadata(metadata);
  if (meta?.trigger_reason_label) return meta.trigger_reason_label;
  const raw = (typeof reason === 'string' ? reason.trim() : '') || meta?.trigger_reason || '';
  if (!raw) return null;
  // A code ("advisor_review_needed") gets its label; words a person typed
  // (legacy free-text reasons) are shown as written.
  return /^[a-z0-9_]+$/.test(raw) ? getCustodyHandoffReasonLabel(raw) : raw;
}

/**
 * The HANDOFFS — the events that carry a "why". An accept, a reassign or a
 * rejection only moves custody; it never replaces the reason a ticket was
 * sent (foh_accept writes an empty reason and becomes last_event_id).
 */
export const HANDOFF_EVENT_KINDS: readonly CustodyEventKind[] = Object.freeze(['kick_to_foh', 'kick_to_boh']);

/**
 * The latest meaningful handoff among `entries` (oldest-first or any order),
 * optionally restricted to some kinds (e.g. only technician → FOH).
 */
export function latestMeaningfulHandoff<E extends { eventKind: CustodyEventKind | string; createdAt: string }>(
  entries: readonly E[],
  kinds: readonly CustodyEventKind[] = HANDOFF_EVENT_KINDS,
): E | null {
  let best: E | null = null;
  for (const e of entries ?? []) {
    if (!kinds.includes(e.eventKind as CustodyEventKind)) continue;
    if (!best || Date.parse(e.createdAt) >= Date.parse(best.createdAt)) best = e;
  }
  return best;
}

export function toCustodyHistoryEntries(
  rows: CustodyHistoryRow[],
): CustodyHistoryEntry[] {
  return (rows ?? []).map((r) => {
    const reason = custodyEventReasonLabel(r.reason, r.metadata);
    const kind = r.event_kind as CustodyEventKind;
    return {
      eventKind: kind,
      label: CUSTODY_EVENT_KIND_LABELS[kind] ?? r.event_kind,
      fromOwner: r.from_owner ?? null,
      toOwner: r.to_owner ?? null,
      actorId: r.actor_id ?? null,
      actorRole: r.actor_role ?? null,
      reason,
      notes: custodyEventNotes(r.metadata),
      eventId: r.id ?? null,
      createdAt: r.created_at,
    };
  });
}

/** "TECHNICIAN" / "SERVICE_WRITER" → "Technician" / "Service Writer". */
export function prettyCustodyRole(role: string | null | undefined): string | null {
  if (!role || !role.trim()) return null;
  return role.trim().toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Who did it, for people: the person's NAME first; the role only when the
 * name is unknown (legacy events, or a reader that cannot see the profile);
 * "System" when neither is recorded.
 */
export function custodyActorDisplay(
  actorId: string | null | undefined,
  actorRole: string | null | undefined,
  names: Readonly<Record<string, string>> | null | undefined,
): { primary: string; role: string | null } {
  const role = prettyCustodyRole(actorRole);
  const name = actorId && names ? names[actorId]?.trim() : '';
  if (name) return { primary: name, role };
  if (role) return { primary: role, role: null };
  return { primary: 'System', role: null };
}
